import React, { createContext, useContext, useState, useCallback, useEffect, useId } from 'react';

const BackButtonContext = createContext(null);

/**
 * BackButtonProvider - Gestion centralisee du bouton back Android
 *
 * Priorites:
 * - 100: Modales/Dialogs
 * - 80: Bottom sheets
 * - 60: Overlays (useBackButton existant)
 * - 0: Navigation par defaut (retour a Map ou fermer app)
 *
 * Comportement par defaut:
 * - Si pas sur /map -> navigate vers /map
 * - Si sur /map -> laisser l'app se fermer (comportement natif)
 */
export function BackButtonProvider({ children }) {
  const [handlers, setHandlers] = useState([]);

  // Enregistrer un handler avec une priorite
  const registerHandler = useCallback((id, handler, priority = 0) => {
    setHandlers(prev => {
      const filtered = prev.filter(h => h.id !== id);
      return [...filtered, { id, handler, priority }].sort((a, b) => b.priority - a.priority);
    });

    // Retourne une fonction de cleanup
    return () => {
      setHandlers(prev => prev.filter(h => h.id !== id));
    };
  }, []);

  // Desenregistrer un handler
  const unregisterHandler = useCallback((id) => {
    setHandlers(prev => prev.filter(h => h.id !== id));
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      // Parcourir les handlers par priorite (deja tries)
      for (const { handler } of handlers) {
        try {
          if (handler()) {
            // Handler a consomme l'event -> ne pas modifier l'historique
            return;
          }
        } catch (e) {
          console.error('BackButton handler error:', e);
        }
      }

      // Aucun handler n'a consomme -> laisser le navigateur gérer le back
      // (évite les doubles retours / boucles)
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handlers]);

  return (
    <BackButtonContext.Provider value={{ registerHandler, unregisterHandler }}>
      {children}
    </BackButtonContext.Provider>
  );
}

/**
 * Hook pour enregistrer un handler de back button
 *
 * @param handler - Fonction qui retourne true si elle a gere l'event
 * @param priority - Priorite (plus haut = execute en premier)
 * @param enabled - Si false, le handler n'est pas enregistre
 */
export function useRegisterBackHandler(handler, priority = 0, enabled = true) {
  const context = useContext(BackButtonContext);
  const id = useId();

  useEffect(() => {
    if (!context || !enabled) return;

    return context.registerHandler(id, handler, priority);
  }, [context, id, handler, priority, enabled]);
}

/**
 * Hook pour acceder au context (pour usage avance)
 */
export function useBackButtonContext() {
  return useContext(BackButtonContext);
}

export default BackButtonProvider;
